<!-- ElementPickerModal.vue -->
<template>
  <div>
    <div class="modal-backdrop fade show"></div>

    <div
      class="modal fade show d-block"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="epm-title"
      @keydown.esc.prevent="close"
    >
      <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <!-- Header -->
          <div class="modal-header d-flex flex-row">
            <h5 class="modal-title" id="epm-title">
              <template v-if="localFocusGroup">
                Select {{ focusedGroupLabel }} Elements
              </template>
              <template v-else>
                Select Elements
              </template>
            </h5>
            <div class="ms-auto">
              <template v-if="localFocusGroup" >
                <!--<i class="bi bi-info-circle me-2"></i>
                <span class="me-auto">Showing only {{ focusedGroupLabel }} elements</span>-->
                <a href="#" @click.prevent="clearFocusGroup" class="text-decoration-none">
                  <i class="bi bi-arrows-fullscreen me-1"></i>
                  Show all groups
                </a>
              </template>
              <button type="button" class="btn-close" aria-label="Close" @click="close"></button>
            </div>
          </div>

          <!-- Search and Options -->
          <div class="px-3 pt-3">
            <!-- Show full editor link when in focused mode -->
           
            
            <div class="row g-2 align-items-center mb-2">
              <div class="col-12 col-md-6">
                <input
                  ref="searchRef"
                  v-model="query"
                  type="text"
                  class="form-control"
                  :placeholder="searchPlaceholder"
                  aria-label="Search elements"
                />
              </div>
              <div class="col-12 col-md-6 d-flex gap-2 align-items-center justify-content-md-end">
                <div class="form-check mb-0 ">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="showOnlySelected"
                    v-model="showOnlySelected"
                  />
                  <label class="form-check-label" for="showOnlySelected">
                    Show selected
                  </label>
                </div>
                <div class="btn-group btn-group-sm" role="group" aria-label="Display mode" >
                  <input type="radio" class="btn-check" name="displayMode" id="displayLabel" 
                        v-model="displayMode" value="label" autocomplete="off">
                  <label class="btn btn-outline-primary" for="displayLabel" :class="{ active: displayMode==='label' }">
                    <i class="bi bi-tag"></i> Labels
                  </label>
                  
                  <input type="radio" class="btn-check" name="displayMode" id="displayApi"  
                        v-model="displayMode" value="api" autocomplete="off">
                  <label class="btn btn-outline-primary" for="displayApi" :class="{ active: displayMode==='api' }">
                    <i class="bi bi-code"></i> Property
                  </label>
                </div>

                <button type="button" class="btn btn-link btn-sm py-0" @click="resetToDefault">
                  <i class="bi bi-arrow-clockwise"></i>
                  Reset
                </button>
                <button type="button" class="btn btn-link btn-sm py-0" @click="clearAll">
                  <i class="bi bi-x-lg"></i>
                  Clear all
                </button>
              </div>
            </div>
          </div>

          <!-- Body -->
          <div class="modal-body">
            <div class="row">
              <!-- Sidebar - only show if not focused on a group -->
              <nav v-if="!localFocusGroup" class="col-3 mb-3 mb-md-0 d-none d-lg-block" style="max-height: 70vh; overflow-y: auto;">
                <ul class="list-group position-sticky top-0" style="z-index:0">
                  <li
                    v-for="(g, gid) in orderedGroups"
                    :key="gid"
                    class="list-group-item p-0 border-0 bg-transparent"
                  >
                    <a
                      :href="'#group-' + gid"
                      class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                      :class="{ active: activeGroupId === gid }"
                      @click.prevent="scrollToGroup(gid)"
                    >
                      <span>{{ g.label || humanize(gid) }}</span>
                      <span class="badge bg-light text-dark ms-2">
                        {{ selectedCountByGroup(gid) }}/{{ g.elements.length }}
                      </span>
                    </a>
                  </li>
                </ul>
              </nav>

              <!-- Content - expand to full width when focused -->
              <section :class="localFocusGroup ? 'col-12' : 'col-12 col-lg-9'">
                <div
                  v-for="(group, gid) in displayGroups"
                  :key="gid"
                  :id="'group-' + gid"
                  class=""
                >
                  <div class="d-flex justify-content-between align-items-baseline mt-5 mb-1">

                    <h6 class="mb-0 text-uppercase">{{ group.label || humanize(gid) }}</h6>

                    <div class="text-nowrap">
                      <button type="button" class="btn btn-link btn-sm px-2" @click="selectAllInGroup(gid)">Select all</button>
                      <span class="text-muted">·</span>
                      <button type="button" class="btn btn-link btn-sm px-2" @click="clearGroup(gid)">Clear</button>
                    </div>
                  </div>

                  <div class="row row-cols-1  row-cols-lg-2 g-2">
                    <div v-for="el in group.elements" :key="el.id" class="col" v-show="!showOnlySelected || selected.has(el.id)">
                      <div class="border rounded p-2 d-flex flex-column gap-1" :class="{ 'selected-bg': selected.has(el.id)}" style="min-height: 125px;">
                        <div class="d-flex align-items-center justify-content-between" >
                          <div class="form-check m-0">
                            <input
                              class="form-check-input"
                              type="checkbox"
                              :id="`chk-${gid}-${el.id}`"
                              :value="el.id"
                              :checked="selected.has(el.id)"
                              @change="toggle(el.id)"
                            />
                            <label class="form-check-label fw-semibold" :for="`chk-${gid}-${el.id}`">
                              <template v-if="displayMode === 'api'">
                                <code>{{ el.id }}</code>
                              </template>
                              <template v-else>
                                {{ el.label || pretty(el.id) }}
                              </template>
                             
                            </label>
                          </div>
                           <span v-if="el.premium" class="badge bg-warning text-dark"><i class="bi bi-trophy"></i> Premium</span>
                        </div>

                        <small v-if="el.description" class="text-muted">{{ el.description }}</small>

                        <div class="mt-auto d-flex flex-wrap gap-2">

                          <span v-if="el.beta" class="badge bg-warning text-dark">beta</span>
                          
                          <span v-if="el.unit" class="badge bg-light text-secondary text-dark border">Unit: {{ el.unit }}</span>
                          
                          <span v-if="el.aggregation" class="badge bg-light text-secondary text-dark border">
                            Statistic: {{ el.aggregation }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div v-if="filteredElementCount(group) === 0" class="col">
                      <div class="alert alert-light border d-flex align-items-center m-0">
                        {{ showOnlySelected ? 'No selected elements in this group.' : 'No elements match the search.' }}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer justify-content-between">
            <div class="text-muted">
                <span class="fw-semibold">{{ selected.size }}</span> selected
                <!--<span class="ms-2 text-muted small text-truncate">(output: {{ currentOutputString }})</span>-->
            </div>
            <div>
              <button type="button" class="btn btn-secondary me-2" @click="close">Cancel</button>
              <button type="button" class="btn btn-primary" @click="save">Save</button>
            </div>
          </div>
        </div>
      </div>
    </div> <!-- /modal -->
  </div>
</template>

<script>
// adjust paths to your project
import { getAllGroupsEnriched } from '@/data/elementInfo';              // normalized groups (id, label, etc.)
import { ELEMENT_META, resolveUnit } from '@/data/elementInfoMeta';  // unit maps + resolver

export default {
  name: 'ElementPickerModal',
  props: {
    // v-model: comma-separated string of ids, or delta tokens (add:/remove:)
    modelValue: { type: String, default: '' },

    // groups/meta overrides (optional)
    groups: { type: Object, default: null },
    meta: { type: Object, default: null },

    // unit system
    unitGroup: { type: String, default: 'metric' }, // 'metric' | 'us' | 'uk'
    defaultElements: { type: Array, default: () => [] },
  
    groupOrder: { type: Array, default: () => [] },

    // Add this new prop
    focusGroup: { 
      type: String, 
      default: null,
      validator: (value) => value === null || typeof value === 'string'
    }
  },
  emits: ['update:modelValue', 'save', 'close', 'update:focusGroup'],
  data() {
    return {
      query: '',
      selected: new Set(),       // working selection
      defaultSet: new Set(),     // baseline selection when value is empty or when using delta
      addSet: new Set(),         // working delta adds relative to defaultSet
      removeSet: new Set(),      // working delta removes relative to defaultSet
      startedAsDelta: false,     // remember input mode
      activeGroupId: null,
      observer: null,
      showOnlySelected: false,   // Add this new data property
      localFocusGroup: null,     // Add this for internal focus group state
      displayMode: 'label'
    };
  },
  computed: {

    currentOutputString() {
      return this.chooseBestOutputString();
    },
    normalizedGroups() {
      const metaMap = this.meta || ELEMENT_META;
      const transform = (el) => {
        const m = metaMap[el.id] || {};
        return {
          ...el,
          unit: el.unit ?? resolveUnit(m.unit, this.unitGroup),
          aggregation: el.aggregation ?? m.aggregation ?? 'none'
        };
      };
      return getAllGroupsEnriched(this.unitGroup);
    },
    currentFocusGroup() {
      return this.localFocusGroup !== null ? this.localFocusGroup : this.focusGroup;
    },
    focusedGroupLabel() {
      if (!this.currentFocusGroup) return '';
      const group = this.normalizedGroups[this.currentFocusGroup];
      return group ? (group.label || this.humanize(this.currentFocusGroup)) : '';
    },
    searchPlaceholder() {
      return this.currentFocusGroup 
        ? `Search ${this.focusedGroupLabel.toLowerCase()} elements…`
        : 'Search elements or groups…';
    },
    displayGroups() {
      // If focused on a group, only show that group
      if (this.currentFocusGroup) {
        const group = this.orderedGroups[this.currentFocusGroup];
        if (group) {
          return { [this.currentFocusGroup]: group };
        }
      }
      // Otherwise show all groups
      return this.orderedGroups;
    },
    orderedGroups() {
      const base = this.normalizedGroups;
      const order = this.groupOrder.length ? this.groupOrder : Object.keys(base);
      const out = {};
      for (const k of order) if (base[k]) out[k] = base[k];
      return this.applyQueryFilter(out, this.query);
    },
    outputModeLabel() {
      const { explicitList, deltaTokens } = this.computeOutputs();
      if (deltaTokens.length === 0) return 'default';
      return deltaTokens.length < explicitList.length ? 'delta' : 'explicit';
    }
  },
  watch: {
    currentOutputString(newVal) {
      console.log('ElementPickerModal: output string changed to', newVal);
    },
    modelValue: {
      immediate: true,
      handler(v) {
        this.initializeFromValue(v);
      }
    },
    defaultElements: {
      immediate: true,
      handler(v) {
        this.defaultSet = new Set(this.defaultElements);
        // re-apply the current modelValue to recompute sets against new default
        this.initializeFromValue(this.modelValue);
      }
    },
    unitGroup() {
      // units update automatically via computed transform
    },
    focusGroup: {
      immediate: true,
      handler(val) {
        this.localFocusGroup = val;
      }
    }
  },
  mounted() {
    if (this.modelValue) {
      this.$nextTick(() => this.setupObserver());
    }
  },
  beforeUnmount() {
    this.teardownObserver();
  },
  methods: {
    // parsing helpers
    splitCSV(str) {
      return (str || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
    },
    parseValueString(str) {
      const tokens = this.splitCSV(str);
      const hasDelta = tokens.some(t => /^add:|^remove:/i.test(t));
      return { tokens, hasDelta };
    },

    initializeFromValue(str) {
      const { tokens, hasDelta } = this.parseValueString(str);
      this.startedAsDelta = hasDelta || (!tokens.length && this.defaultSet.size > 0);

      // base selection
      this.selected.clear();
      this.addSet.clear();
      this.removeSet.clear();

      if (this.startedAsDelta) {
        // start from default
        this.defaultSet.forEach(id => this.selected.add(id));
        // apply deltas
        for (const t of tokens) {
          const m = t.match(/^(add|remove):(.+)$/i);
          if (!m) continue;
          const op = m[1].toLowerCase();
          const id = m[2].trim();
          if (op === 'add') {
            this.selected.add(id);
            this.addSet.add(id);
            this.removeSet.delete(id);
          } else if (op === 'remove') {
            this.selected.delete(id);
            this.removeSet.add(id);
            this.addSet.delete(id);
          }
        }
      } else {
        // explicit list
        tokens.forEach(id => this.selected.add(id));
        // derive deltas vs default so we can display the "default" badge and compute smart output
        for (const id of this.selected) {
          if (!this.defaultSet.has(id)) this.addSet.add(id);
        }
        for (const id of this.defaultSet) {
          if (!this.selected.has(id)) this.removeSet.add(id);
        }
      }
    },
        
    clearFocusGroup() {
      this.localFocusGroup = '';
      // Emit the change if parent is controlling the prop
      this.$emit('update:focusGroup', null);
      
      // Reset search when expanding to full view
      this.query = '';
      
      // Setup observer for the newly visible groups
      this.$nextTick(() => {
        this.setupObserver();
        this.computeActiveGroup();
      });
    },
    // search
    applyQueryFilter(groups, q) {
      // If in focused mode and searching, only search within the focused group
      if (this.currentFocusGroup && q && q.trim()) {
        const term = q.trim().toLowerCase();
        const group = groups[this.currentFocusGroup];
        if (!group) return {};
        
        const els = group.elements.filter(e =>
          e.id.toLowerCase().includes(term) ||
          (e.label && e.label.toLowerCase().includes(term))
        );
        
        return els.length ? { [this.currentFocusGroup]: { ...group, elements: els } } : {};
      }
      
      // Otherwise use the original search logic
      if (!q || !q.trim()) return groups;
      const term = q.trim().toLowerCase();
      const filtered = {};
      for (const [gid, g] of Object.entries(groups)) {
        const labelHit = (g.label || gid).toLowerCase().includes(term);
        if (labelHit) {
          filtered[gid] = { ...g, elements: g.elements.slice() };
          continue;
        }
        const els = g.elements.filter(e =>
          e.id.toLowerCase().includes(term) ||
          (e.label && e.label.toLowerCase().includes(term))
        );
        if (els.length) filtered[gid] = { ...g, elements: els };
      }
      return filtered;
    },

    // ui text helpers
    humanize(id) {
      return id
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, s => s.toUpperCase());
    },
    pretty(id) {
      return this.humanize(id.replace(/p5\b/g, '.5').replace(/([0-9]{2})m\b/gi, '$1 m'));
    },

    // selection ops with delta maintenance
    toggle(elId) {
      if (this.selected.has(elId)) {
        // deselect
        this.selected.delete(elId);
        if (this.defaultSet.has(elId)) {
          this.removeSet.add(elId);   // removing a default element
        } else {
          this.addSet.delete(elId);   // undo an explicit add
        }
      } else {
        // select
        this.selected.add(elId);
        if (this.defaultSet.has(elId)) {
          this.removeSet.delete(elId); // re-include default element
        } else {
          this.addSet.add(elId);       // add a non-default element
        }
      }
    },
    selectAllInGroup(gid) {
      const g = this.orderedGroups[gid];
      if (!g) return;
      for (const e of g.elements) {
        if (!this.selected.has(e.id)) this.toggle(e.id);
      }
    },
    clearGroup(gid) {
      const g = this.orderedGroups[gid];
      if (!g) return;
      for (const e of g.elements) {
        if (this.selected.has(e.id)) this.toggle(e.id);
      }
    },
    selectedCountByGroup(gid) {
      const g = this.normalizedGroups[gid];
      if (!g) return 0;
      let n = 0;
      for (const e of g.elements) if (this.selected.has(e.id)) n++;
      return n;
    },
    isFromDefault(id) {
      return this.selected.has(id) && this.defaultSet.has(id) && !this.removeSet.has(id);
    },

    // output calculation
    computeOutputs() {
      // explicit list
      const explicitList = Array.from(this.selected).sort();

      // delta tokens: if selection equals default, return empty
      const add = Array.from(this.addSet).sort();
      const rem = Array.from(this.removeSet).sort();
      const deltaTokens = [];
      for (const id of add) deltaTokens.push(`add:${id}`);
      for (const id of rem) deltaTokens.push(`remove:${id}`);

      return { explicitList, deltaTokens };
    },
    chooseBestOutputString() {
      // Force reactivity by accessing Set sizes
      const selectedSize = this.selected.size;
      const addSize = this.addSet.size;
      const removeSize = this.removeSet.size;
      
      const { explicitList, deltaTokens } = this.computeOutputs();

      // exact default -> empty string
      if (deltaTokens.length === 0 &&
          explicitList.length === this.defaultSet.size &&
          explicitList.every((id) => this.defaultSet.has(id))) {
        return '';
      }

      // choose shorter representation; tie-break: prefer delta if we started with delta
      if (deltaTokens.length < explicitList.length) {
        return deltaTokens.join(',');
      } else if (deltaTokens.length > explicitList.length) {
        return explicitList.join(',');
      } else {
        return this.startedAsDelta ? deltaTokens.join(',') : explicitList.join(',');
      }
    },

    // modal controls
    close() {
      this.$emit('close', this.modelValue);
      this.$emit('update:modelValue', this.modelValue); // no change
      this.teardownObserver();
    },
    save() {
      const out = this.chooseBestOutputString();
      this.$emit('save', out);
      this.$emit('update:modelValue', out);
      this.teardownObserver();
    },

    // sidebar jump + active section
    scrollToGroup(gid) {
      const el = document.getElementById('group-' + gid);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.activeGroupId = gid;
    },
    computeActiveGroup() {
      const ids = Object.keys(this.orderedGroups);
      this.activeGroupId = ids.length ? ids[0] : null;
    },
    setupObserver() {
      this.teardownObserver();
      const contentRoot = this.$el?.querySelector('.modal-body .col-12.col-md-9');
      if (!contentRoot) return;
      this.observer = new IntersectionObserver(
        entries => {
          const visibles = entries
            .filter(e => e.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visibles.length) {
            const id = visibles[0].target.getAttribute('id')?.replace(/^group-/, '');
            if (id) this.activeGroupId = id;
          }
        },
        { root: contentRoot, threshold: [0.05, 0.6] }
      );
      this.$nextTick(() => {
        const nodes = contentRoot.querySelectorAll('[id^="group-"]');
        nodes.forEach(n => this.observer.observe(n));
      });
    },
    teardownObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    },

    // Add these new methods
    resetToDefault() {
      // Clear all selections and apply only default elements
      this.selected.clear();
      this.addSet.clear();
      this.removeSet.clear();
      
      // Add all default elements
      this.defaultSet.forEach(id => {
        this.selected.add(id);
      });
      
      // Since we're exactly at default, both sets remain empty
      // which will result in an empty output string
    },
    
    clearAll() {
      // Clear absolutely everything
      this.selected.clear();
      this.addSet.clear();
      this.removeSet.clear();
      
      // Mark all default elements as removed
      this.defaultSet.forEach(id => {
        this.removeSet.add(id);
      });
    },
    
    filteredElementCount(group) {
      if (!group.elements) return 0;
      
      let count = 0;
      for (const el of group.elements) {
        if (!this.showOnlySelected || this.selected.has(el.id)) {
          count++;
        }
      }
      return count;
    },

    // Override applyQueryFilter to handle focused mode
    applyQueryFilter(groups, q) {
      // If in focused mode and searching, only search within the focused group
      if (this.currentFocusGroup && q && q.trim()) {
        const term = q.trim().toLowerCase();
        const group = groups[this.currentFocusGroup];
        if (!group) return {};
        
        const els = group.elements.filter(e =>
          e.id.toLowerCase().includes(term) ||
          (e.label && e.label.toLowerCase().includes(term))
        );
        
        return els.length ? { [this.currentFocusGroup]: { ...group, elements: els } } : {};
      }
      
      // Otherwise use the original search logic
      if (!q || !q.trim()) return groups;
      const term = q.trim().toLowerCase();
      const filtered = {};
      for (const [gid, g] of Object.entries(groups)) {
        const labelHit = (g.label || gid).toLowerCase().includes(term);
        if (labelHit) {
          filtered[gid] = { ...g, elements: g.elements.slice() };
          continue;
        }
        const els = g.elements.filter(e =>
          e.id.toLowerCase().includes(term) ||
          (e.label && e.label.toLowerCase().includes(term))
        );
        if (els.length) filtered[gid] = { ...g, elements: els };
      }
      return filtered;
    }
  }
};
</script>
<style scoped>

/* Or using CSS variables for theme compatibility */
.selected-bg {
  background-color: rgba(var(--vc-bs-primary-rgb), 0.1) !important;
}
</style>